// Экран входа (CASE-49). Логин + 7-значный пароль; серверная проверка пары.
// Три захардкоженных учётки (user1/user2/user3) — см. backend HardcodedUsers.

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { T } from '../../tokens';
import { auth, ApiError, humanizeError } from '../../api';

export interface LoginScreenProps {
  /** Вызывается после успешного входа с логином пользователя. */
  readonly onLoggedIn: (login: string) => void;
}

const PASSWORD_LENGTH = 7;

export function LoginScreen({ onLoggedIn }: LoginScreenProps): JSX.Element {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = login.trim().length > 0 && password.length === PASSWORD_LENGTH && !busy;

  const submit = (): void => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    auth
      .login(login.trim(), password)
      .then((me) => onLoggedIn(me.login))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          setError('Неверный логин или пароль');
        } else {
          setError(humanizeError(err, 'load'));
        }
        setBusy(false);
      });
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: T.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: T.font,
      }}
    >
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: T.brandDeep, letterSpacing: -0.5 }}>
            aidepo
          </div>
          <div style={{ fontSize: 13.5, color: T.ink3, marginTop: 6 }}>
            Войдите, чтобы открыть свой дом
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 18,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <Field label="Учётная запись">
            <input
              type="text"
              value={login}
              autocomplete="username"
              placeholder="user1"
              disabled={busy}
              onInput={(e) => setLogin((e.target as HTMLInputElement).value)}
              style={inputStyle}
            />
          </Field>

          <Field label="Пароль (7 цифр)">
            <input
              type="password"
              value={password}
              inputMode="numeric"
              autocomplete="current-password"
              maxLength={PASSWORD_LENGTH}
              placeholder="•••••••"
              disabled={busy}
              onInput={(e) =>
                setPassword((e.target as HTMLInputElement).value.replace(/\D/g, ''))
              }
              style={inputStyle}
            />
          </Field>

          {error !== null && (
            <div
              style={{
                padding: '10px 12px',
                background: T.dangerSoft,
                border: '1px solid #E9C7BF',
                borderRadius: 12,
                fontSize: 12.5,
                color: T.danger,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              marginTop: 4,
              padding: '13px 16px',
              borderRadius: 12,
              border: 'none',
              background: canSubmit ? T.brand : T.borderStrong,
              color: '#FFFFFF',
              fontFamily: T.font,
              fontSize: 15,
              fontWeight: 700,
              cursor: canSubmit ? 'pointer' : 'default',
            }}
          >
            {busy ? 'Вхожу…' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputStyle: JSX.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 12,
  border: `1px solid ${T.border}`,
  fontFamily: T.font,
  fontSize: 15,
  color: T.ink,
  background: T.surface,
  outline: 'none',
  boxSizing: 'border-box',
};

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: JSX.Element;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          color: T.ink3,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
